import { Cloud, Shield, Lock, CreditCard, CheckCircle2, KeyRound } from "lucide-react";

const PARTNERS = [
  {
    name: "AWS",
    color: "#FF9900",
    tag: "Amazon Web Services",
    desc: "Cloud compute, storage, CDN, and managed databases powering the Ortelly platform.",
    icon: Cloud,
  },
  {
    name: "Microsoft Azure",
    color: "#0078D4",
    tag: "Cloud & Identity",
    desc: "Identity (Entra ID), enterprise SSO, and hybrid integrations for regulated workloads.",
    icon: Shield,
  },
  {
    name: "Razorpay",
    color: "#3395FF",
    tag: "Payments",
    desc: "PCI-DSS compliant payments for studio invoicing, subscriptions, and client payouts.",
    icon: CreditCard,
  },
];

const SECURITY = [
  { icon: Lock,    t: "End-to-end encryption",   d: "TLS 1.2+ in transit, AES-256 at rest." },
  { icon: KeyRound,t: "Role-based access",       d: "Least-privilege by default with granular permissions." },
  { icon: Shield,  t: "Audit & compliance ready", d: "Detailed audit logs, tokenised data, secure secrets." },
];

export default function IntegrationsSecurity() {
  return (
    <section
      className="relative overflow-hidden border-y border-slate-200 bg-slate-900 text-white"
      data-testid="integrations-security-section"
    >
      {/* decorative background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, #0046B5 0, transparent 40%), radial-gradient(circle at 85% 80%, #00C4B5 0, transparent 35%)",
        }}
      />

      <div className="container-section relative py-16 md:py-24">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00C4B5]">
            Integrations & Security
          </span>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Built on trusted clouds. Secured like an enterprise.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-300 md:text-lg">
            Ortelly integrates natively with <strong className="text-white">AWS</strong>,{" "}
            <strong className="text-white">Microsoft Azure</strong>, and{" "}
            <strong className="text-white">Razorpay</strong> — giving you enterprise-grade
            infrastructure, identity, and payments with high-assurance security baked in at every
            layer.
          </p>
        </div>

        {/* Partner cards */}
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {PARTNERS.map((p) => (
            <div
              key={p.name}
              data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="group relative overflow-hidden rounded-lg border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/30 hover:bg-white/10"
            >
              <div className="flex items-center gap-3">
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-md text-white"
                  style={{ backgroundColor: p.color }}
                >
                  <p.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-[Outfit] text-lg font-semibold text-white">{p.name}</p>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em]" style={{ color: p.color }}>
                    {p.tag}
                  </p>
                </div>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-slate-300">{p.desc}</p>
            </div>
          ))}
        </div>

        {/* Security pillars */}
        <div className="mt-14 grid gap-6 rounded-lg border border-white/10 bg-white/5 p-6 backdrop-blur-sm md:grid-cols-3 md:p-8">
          {SECURITY.map((s) => (
            <div key={s.t} className="flex items-start gap-3" data-testid={`security-${s.t.toLowerCase().replace(/\s+/g, '-')}`}>
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#00C4B5]/15 text-[#00C4B5]">
                <s.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-white">{s.t}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-300">{s.d}</p>
              </div>
            </div>
          ))}
        </div>

        {/* trust chips row */}
        <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-[#00C4B5]" /> High-security by design
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-[#00C4B5]" /> Cloud-native deployments
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-[#00C4B5]" /> PCI-DSS payments via Razorpay
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-[#00C4B5]" /> Entra ID / SSO support
          </span>
        </div>
      </div>
    </section>
  );
}
